import React, { useState, useEffect, useCallback, useRef } from "react";
import { LangContext } from "../../contexts/LangContext";
import { getCookie } from "../../services/cookie";
import { getTemplate } from "./visualizationTemplates";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

const API_BASE = "https://flautreach.com/flautreach/api";
const PAGE_SIZE = 10;
const PRELOAD_THRESHOLD = 0.85;
const IMAGE_PRELOAD_COUNT = 3;

// ==================== Кеш изображений ====================
const imageCache = new Map();

const SEARCH_PERIOD_MAP = {
  "any": { en: "Any time", pl: "Dowolny czas", de: "Jederzeit", default: "Any time" },
  "h": { en: "Past hour", pl: "Ostatnia godzina", de: "Letzte Stunde", default: "Past hour" },
  "d": { en: "Past 24 hours", pl: "Ostatnie 24h", de: "Letzte 24 Stunden", default: "Past 24 hours" },
  "w": { en: "Past week", pl: "Ostatni tydzień", de: "Letzte Woche", default: "Past week" },
  "m": { en: "Past month", pl: "Ostatni miesiąc", de: "Letzter Monat", default: "Past month" },
  "y": { en: "Past year", pl: "Ostatni rok", de: "Letztes Jahr", default: "Past year" },
};

function formatSearchPeriod(period, lang = "en") {
  if (!period) return "N/A";
  const config = SEARCH_PERIOD_MAP[period];
  if (!config) return period;
  return config[lang] || config.default || period;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function preloadImage(url) {
  if (!url || imageCache.has(url)) return;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { imageCache.set(url, true); resolve(true); };
    img.onerror = () => { imageCache.set(url, false); resolve(false); };
    img.src = url;
  });
}

const getFilteringConfig = (task) => {
  if (!task) return null;
  return task.filtering_config || task.filtering_config_name || null;
};

// ==================== Определение subtype ====================
function getSubtype(task) {
  if (!task) return "default";
  if (task.task_type === "filtering") {
    const fromConfigName = task.filtering_config_name?.subtype;
    const fromConfig = task.filtering_config?.subtype;
    const fromScraping = task.scraping_subtype?.value || task.scraping_subtype;
    const fromAdditional = task.additional_info?.subtype;
    const result = fromConfigName || fromConfig || fromScraping || fromAdditional;
    if (result) return result;
  }
  return task?.scraping_subtype?.value || 
         task?.subtype ||
         task?.search_config?.subtype ||
         "default";
}

// ==================== НОРМАЛИЗАЦИЯ ДАННЫХ ИЗ PARSE_JOB ====================
function normalizeResults(results) {
  return results.map(item => {
    const normalized = { ...item };
    
    if (item.parse_job?.raw_data) {
      const rawData = item.parse_job.raw_data;
      normalized.raw_data = {
        ...rawData,
        url: item.parse_job?.url || rawData.url || "",
      };
    } else {
      if (!normalized.raw_data) {
        normalized.raw_data = {};
      }
    }

    // ДОБАВИТЬ: поднимаем source из parse_job наверх (для filtering-результатов)
    if (item.parse_job?.source && !normalized.source) {
      normalized.source = item.parse_job.source;
    }

    // ДОБАВИТЬ: поднимаем prev_ai_response из parse_job наверх
    if (item.parse_job?.prev_ai_response) {
      normalized.prev_ai_response = item.parse_job.prev_ai_response;
    }
    
    // ДОБАВИТЬ: также поднимаем url и status из parse_job
    if (item.parse_job?.url && !normalized.url) {
      normalized.url = item.parse_job.url;
    }
    if (item.parse_job?.status && !normalized.status) {
      normalized.status = item.parse_job.status;
    }
    
    if (item.filter_passed !== undefined) {
      normalized.filter_status = item.filter_passed;
    }
    if (item.ai_filter_passed !== undefined) {
      normalized.ai_filter_status = item.ai_filter_passed;
    }
    if (item.overall_passed !== undefined) {
      normalized.overall_passed = item.overall_passed;
    }
    
    if (item.parse_job?.url && !normalized.raw_data?.url) {
      if (!normalized.raw_data) normalized.raw_data = {};
      normalized.raw_data.url = item.parse_job.url;
    }
    
    return normalized;
  });
}


// ==================== ImageWithLoader ====================
function ImageWithLoader({ src, alt, style, txt }) {
  const [loaded, setLoaded] = useState(() => imageCache.has(src));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (imageCache.has(src)) { setLoaded(true); return; }
    setLoaded(false);
    setError(false);
    preloadImage(src).then(success => { setLoaded(success); setError(!success); });
  }, [src]);

  if (error) {
    return (
      <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", color: "#991b1b", fontSize: 13, fontWeight: 600, minHeight: 200, borderRadius: 12 }}>
        {txt.failedToLoadImage}
      </div>
    );
  }

  if (!loaded) {
    return (
      <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#f3f4f6", position: "relative", overflow: "hidden", minHeight: 200, borderRadius: 12 }}>
        <div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>{txt.loadingImage}</div>
        </div>
      </div>
    );
  }

  return <img src={src} alt={alt} style={{ ...style, opacity: loaded ? 1 : 0, transition: "opacity 0.3s ease" }} />;
}

// ==================== ДЕФОЛТНЫЕ ПРОМПТЫ ====================
const getDefaultPrompt = (subtype, lang) => {
  const t = lang === "pl" ? pl : en;
  const prompts = {
    instagram_posts: t.defaultPrompts.instagram_posts,
    instagram_pages: t.defaultPrompts.instagram_pages,
    facebook_posts: t.defaultPrompts.facebook_posts,
    linkedin_posts: t.defaultPrompts.linkedin_posts,
    facebook_pages: t.defaultPrompts.facebook_pages,
    default: t.defaultPrompts.default,
  };
  return prompts[subtype] || prompts.default;
};

// ==================== useIsMobile ====================
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

// ==================== Компактная кнопка-сегмент ====================
function SegmentedButton({ children, active, disabled, onClick, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="platform-btn"
      style={{
        flex: 1,
        justifyContent: "center",
        fontSize: 11,
        padding: "6px 10px",
        minHeight: 32,
        lineHeight: 1.3,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: active ? 700 : 500,
        borderColor: active ? "var(--blue)" : "var(--border)",
        background: active ? "var(--blue-light)" : "#fff",
        color: active ? "var(--blue)" : "var(--dark)",
        transition: "all 0.15s ease",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ==================== ГЛАВНЫЙ КОМПОНЕНТ ====================
export function VisualizationPage({ tasks: propTasks, token: propToken, initialConfig, onBack }) {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const txt = t.visualizationPage;
  const token = propToken || getCookie("authToken");
  const isMobile = useIsMobile();

  // === State ===
  const [tasks, setTasks] = useState(propTasks || []);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [vizType, setVizType] = useState("standard");
  const [vizMode, setVizMode] = useState("all");
  const [randomCount, setRandomCount] = useState(20);
  const [aiPrompt, setAiPrompt] = useState("");
  const [filterKeyword, setFilterKeyword] = useState(t.defaultPrompts.default_pass_word);
  const [filterMode, setFilterMode] = useState("first_line");
  const [filterPassed, setFilterPassed] = useState(null);
  const [aiResponse, setAiResponse] = useState("");
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiTokensInfo, setAiTokensInfo] = useState(null);
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [scrapingStatus, setScrapingStatus] = useState("success");
  
  // filtering states
  const [filterStatus, setFilterStatus] = useState("passed");
  const [aiFilterStatus, setAiFilterStatus] = useState("passed");
  
  // Для scraping_filtering задач - выбор источника данных
  const [dataSource, setDataSource] = useState("filtering"); // "scraping" | "filtering"

  const loadingMoreRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // ===== Загрузка задачи из initialConfig =====
  useEffect(() => {
    if (initialConfig?.fullTask) {
      setSelectedTask(initialConfig.fullTask);
    }
  }, [initialConfig]);

  useEffect(() => {
    if (initialConfig?.taskId) {
      setSelectedTaskId(initialConfig.taskId);
    }
  }, [initialConfig?.taskId]);

  useEffect(() => {
    if (initialConfig?.aiPrompt) {
      setVizType("ai");
      initialPromptSetRef.current = true;
      setAiPrompt(initialConfig.aiPrompt);
    }
    if (initialConfig?.mode) {
      setVizMode(initialConfig.mode);
    }
    if (initialConfig?.count) {
      setRandomCount(initialConfig.count);
    }
    if (initialConfig?.filterKeyword) {
      setFilterKeyword(initialConfig.filterKeyword);
    }
    if (initialConfig?.filterMode) {
      setFilterMode(initialConfig.filterMode);
    }
    if (initialConfig?.dataSource) {
      setDataSource(initialConfig.dataSource);
    }
  }, [initialConfig]);

  const subtype = getSubtype(selectedTask);
  const template = getTemplate(subtype, lang);
  const currentResult = results[currentIndex];
  const initialPromptSetRef = useRef(false);
  
  // Определяем, является ли задача scraping_filtering
  const isScrapingFiltering = selectedTask?.task_type === "scraping_filtering";

  // === Получаем список тасок ===
  useEffect(() => {
    if (propTasks?.length > 0) { setTasks(propTasks); return; }
    if (!token) return;
    fetch(`${API_BASE}/tasks/`, { headers: { "Authorization": `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setTasks(data))
      .catch(err => console.error("Failed to fetch tasks:", err));
  }, [propTasks, token]);

  // === Автозапуск ===
  useEffect(() => {
    if (initialConfig?.autoStart && selectedTaskId && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      setTimeout(() => loadInitialData(), 150);
    }
  }, [selectedTaskId]);

  // === Сброс при смене ===
  // === Сброс при смене ===
useEffect(() => {
    if (started) {
      setResults([]); setCurrentIndex(0); setCurrentPage(1); setHasMore(false);
      setError(""); setAiResponse(""); setAiError(""); setAiTokensInfo(null);
      setFilterPassed(null); setIsAiLoading(false); setStarted(false);
      setIsTransitioning(false); loadingMoreRef.current = false;
      // Убираем сброс filterStatus и aiFilterStatus
    }
  }, [selectedTaskId, vizType, vizMode, dataSource]);

  // === Дефолтный промпт ===
  useEffect(() => {
    if (initialPromptSetRef.current) return;
    if (vizType === "ai" && subtype) {
      setAiPrompt(getDefaultPrompt(subtype, lang));
    }
  }, [vizType, subtype]);

  // === AI анализ ===
  useEffect(() => {
    if (started && vizType === "ai" && aiPrompt.trim() && currentResult) {
      runAIAnalysis();
    }
  }, [currentIndex, started]);

  // === Проверка фильтра ===
  useEffect(() => {
    if (!isAiLoading && aiResponse && filterKeyword.trim()) {
      const lines = aiResponse.split('\n');
      const target = filterMode === "first_line" ? (lines[0] || "").trim().toLowerCase() : aiResponse.toLowerCase();
      setFilterPassed(target.includes(filterKeyword.toLowerCase().trim()));
    }
  }, [isAiLoading, aiResponse, filterKeyword, filterMode]);

  // === Предзагрузка изображений ===
  useEffect(() => {
    if (!started || !results.length) return;
    for (let i = currentIndex + 1; i < Math.min(currentIndex + 1 + IMAGE_PRELOAD_COUNT, results.length); i++) {
      const url = results[i]?.raw_data?.image_url;
      if (url && !imageCache.has(url)) preloadImage(url);
    }
  }, [currentIndex, results, started]);

  // === Предзагрузка данных ===
  useEffect(() => {
    if (!started || vizMode === "random" || !hasMore || loadingMoreRef.current) return;
    const progress = (currentIndex + 1) / results.length;
    const remaining = results.length - currentIndex;
    if ((progress >= PRELOAD_THRESHOLD || remaining <= 3) && hasMore) loadMore();
  }, [currentIndex, results.length, hasMore, started, vizMode]);

  // === Определение endpoint ===
  const getEndpoint = useCallback((taskId, mode) => {
    const base = `${API_BASE}/tasks/${taskId}`;
    
    // Для scraping_filtering задач выбираем эндпоинт в зависимости от dataSource
    if (selectedTask?.task_type === "scraping_filtering") {
      if (dataSource === "filtering") {
        return mode === "random"
          ? `${base}/filtering-results/random/`
          : `${base}/filtering-results/`;
      }
      // scraping
      return mode === "random"
        ? `${base}/results/random/`
        : `${base}/results/`;
    }
    
    if (selectedTask?.task_type === "filtering") {
      return mode === "random" 
        ? `${base}/filtering-results/random/`
        : `${base}/filtering-results/`;
    }
    
    return mode === "random"
      ? `${base}/results/random/`
      : `${base}/results/`;
  }, [selectedTask, dataSource]);

  // === Вспомогательная функция для построения URL с фильтрами ===
  const buildFilterParams = useCallback(() => {
    let params = "";
    const isFilteringMode = selectedTask?.task_type === "filtering" || 
                           (selectedTask?.task_type === "scraping_filtering" && dataSource === "filtering");
    
    if (isFilteringMode) {
      if (filterStatus !== "all") params += `&regular_filter=${filterStatus}`;
      if (aiFilterStatus !== "all" && getFilteringConfig(selectedTask)?.use_ai) {
        params += `&ai_filter=${aiFilterStatus}`;
      }
    }
    if (!isFilteringMode) {
      params += `&status=${scrapingStatus}`;
    }
    return params;
  }, [selectedTask, filterStatus, aiFilterStatus, scrapingStatus, dataSource]);

  // === Загрузка ===
  const loadInitialData = async () => {
    if (!selectedTaskId || loading) return;
    setLoading(true); setError(""); setAiResponse(""); setAiError(""); setAiTokensInfo(null);
    setFilterPassed(null); setIsAiLoading(false); loadingMoreRef.current = false;
    try {
      const filterParams = buildFilterParams();
      const endpoint = getEndpoint(selectedTaskId, vizMode);
      
      if (vizMode === "random") {
        const url = `${endpoint}?count=${Math.min(randomCount, 100)}${filterParams}`;
        const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
        if (!res.ok) throw new Error(`${txt.fetchFailed} (${res.status})`);
        const data = await res.json();
        const normalizedResults = normalizeResults(data.results || []);
        setResults(normalizedResults);
        setTotalAvailable(data.total_available || normalizedResults.length);
        setHasMore(false);
        normalizedResults.slice(0, IMAGE_PRELOAD_COUNT + 1).forEach(r => { if (r.raw_data?.image_url) preloadImage(r.raw_data.image_url); });
      } else {
        const url = `${endpoint}?page=1&page_size=${PAGE_SIZE}${filterParams}`;
        const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
        if (!res.ok) throw new Error(`${txt.fetchFailed} (${res.status})`);
        const data = await res.json();
        const normalizedResults = normalizeResults(data.results || []);
        setResults(normalizedResults);
        setTotalAvailable(data.total_available || 0);
        setHasMore(data.pagination?.has_next || false);
        setCurrentPage(1);
        normalizedResults.slice(0, IMAGE_PRELOAD_COUNT + 1).forEach(r => { if (r.raw_data?.image_url) preloadImage(r.raw_data.image_url); });
      }
      setCurrentIndex(0); setStarted(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || vizMode === "random") return;
    loadingMoreRef.current = true; setLoadingMore(true);
    try {
      const next = currentPage + 1;
      const filterParams = buildFilterParams();
      const endpoint = getEndpoint(selectedTaskId, "all");
      const url = `${endpoint}?page=${next}&page_size=${PAGE_SIZE}${filterParams}`;
      const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      const normalizedNewResults = normalizeResults(data.results || []);
      normalizedNewResults.forEach(r => { if (r.raw_data?.image_url) preloadImage(r.raw_data.image_url); });
      setResults(prev => [...prev, ...normalizedNewResults]);
      setCurrentPage(next);
      setHasMore(data.pagination?.has_next || false);
    } catch (err) { console.error(err); }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  }, [currentPage, hasMore, selectedTaskId, token, vizMode, buildFilterParams, getEndpoint]);

  // === AI ===
  const runAIAnalysis = useCallback(async () => {
    if (!aiPrompt.trim() || !currentResult) return;
    setIsAiLoading(true); setAiResponse(""); setAiError(""); setAiTokensInfo(null); setFilterPassed(null);
    const variables = {};
    template.aiVariables?.forEach(v => { variables[v.key] = v.getValue(currentResult); });
    try {
      const res = await fetch(`${API_BASE}/tasks/${selectedTaskId}/results/${currentResult.id}/analyze/`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: aiPrompt, variables }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `${txt.fetchFailed} (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.chunk) setAiResponse(prev => prev + data.chunk);
              if (data.error) { setAiError(data.error); setIsAiLoading(false); return; }
              if (data.done) { setAiTokensInfo(data.tokens_used); setIsAiLoading(false); return; }
            } catch (e) {}
          }
        }
      }
      setIsAiLoading(false);
    } catch (err) { setAiError(err.message); setIsAiLoading(false); }
  }, [aiPrompt, currentResult, selectedTaskId, token, template, txt]);

  // === Навигация ===
  const goToNext = useCallback(() => {
    if (currentIndex < results.length - 1 && !isTransitioning) {
      setIsTransitioning(true); setCurrentIndex(p => p + 1);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [currentIndex, results.length, isTransitioning]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0 && !isTransitioning) {
      setIsTransitioning(true); setCurrentIndex(p => p - 1);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [currentIndex, isTransitioning]);

  useEffect(() => {
    const h = (e) => {
      if (!started) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); goToNext(); }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); goToPrev(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [goToNext, goToPrev, started]);

  const insertPlaceholder = (placeholder) => {
    const ta = document.getElementById("aiPrompt");
    if (ta) {
      const start = ta.selectionStart, end = ta.selectionEnd;
      const text = aiPrompt;
      setAiPrompt(text.substring(0, start) + `[[${placeholder}]]` + text.substring(end));
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + placeholder.length + 4; }, 0);
    }
  };

  const handleCopy = () => {
    if (currentResult) {
      const dataToCopy = currentResult.raw_data?._original || currentResult.raw_data;
      navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    }
  };

  const loadedCount = results.length;
  const isAtEnd = !hasMore && currentIndex >= results.length - 1;
  const isNextLoading = loadingMore && !isAtEnd && currentIndex >= results.length - 3;

  // ==================== RENDER ====================
  return (
    <div className="add-rule-wrap" style={{ maxWidth: 900, padding: isMobile ? "0 8px" : "0" }}>
      {/* Кнопка назад */}
      {onBack && (
        <button 
          onClick={onBack}
          style={{
            marginBottom: 20,
            padding: "8px 16px",
            borderRadius: 8,
            border: "1.5px solid var(--border)",
            background: "#fff",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
            transition: ".15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--blue)";
            e.currentTarget.style.color = "var(--blue)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--border)";
            e.currentTarget.style.color = "inherit";
          }}
        >
          &larr; {txt.backToTasks}
        </button>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexDirection: isMobile ? "column" : "row" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : "clamp(18px, 4vw, 24px)", fontWeight: 800 }}>{txt.visualization}</h2>
          <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: isMobile ? 12 : "clamp(12px, 3vw, 14px)" }}>{txt.browseResults}</p>
        </div>
      </div>

      {/* Конфигурация */}
      <div className="section-card" style={{ padding: isMobile ? "16px" : "28px" }}>
        <h3 style={{ fontSize: isMobile ? 14 : 15 }}>{txt.configuration}</h3>
        <p className="section-sub" style={{ fontSize: isMobile ? 12 : 13 }}>{txt.selectTaskAndOptions}</p>

        {/* Таск */}
        <div className="form-group">
          <label className="form-label" style={{ fontSize: isMobile ? 12 : 13 }}>{txt.task}</label>
          <select className="form-input" value={selectedTaskId} onChange={e => {
            const id = e.target.value;
            setSelectedTaskId(id);
            setSelectedTask(tasks.find(t => t.id === parseInt(id) || t.id === id) || null);
          }} style={{ paddingLeft: 14, fontSize: isMobile ? 13 : 14 }}>
            <option value="">{txt.selectTask}</option>
            {tasks.map(task => (
              <option key={task.id} value={task.id}>
                {task.name || `${txt.task} #${task.id}`} 
                ({task.task_type === "filtering" 
                  ? `${task.filtering_config_name?.subtype || "?"}` 
                  : task.task_type === "scraping_filtering"
                  ? `scraping+filtering (${task.scraping_subtype?.label || task.subtype || "?"})`
                  : task.scraping_subtype?.label || task.subtype || "?"}) 
              </option>
            ))}
          </select>
        </div>

        {/* Data Source для scraping_filtering */}
        {isScrapingFiltering && (
          <div className="form-group">
            <label className="form-label" style={{ fontSize: isMobile ? 12 : 13 }}>
              {txt.dataSource }
            </label>
            <div style={{ display: "flex", gap: 6 }}>
              <SegmentedButton 
                active={dataSource === "scraping"} 
                disabled={!selectedTaskId}
                onClick={() => setDataSource("scraping")}
              >
                {txt.scrapingData }
              </SegmentedButton>
              <SegmentedButton 
                active={dataSource === "filtering"} 
                disabled={!selectedTaskId}
                onClick={() => setDataSource("filtering")}
              >
                {txt.filteringData }
              </SegmentedButton>
            </div>
          </div>
        )}

        {/* Тип и Режим */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>{txt.type}</label>
            <div style={{ display: "flex", gap: 4 }}>
              <SegmentedButton active={vizType === "standard"} disabled={!selectedTaskId} onClick={() => setVizType("standard")}>
                {txt.standard}
              </SegmentedButton>
              <SegmentedButton active={vizType === "ai"} disabled={!selectedTaskId} onClick={() => setVizType("ai")}>
                {txt.ai}
              </SegmentedButton>
            </div>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>{txt.mode}</label>
            <div style={{ display: "flex", gap: 4 }}>
              <SegmentedButton active={vizMode === "all"} disabled={!selectedTaskId} onClick={() => setVizMode("all")}>
                {txt.all}
              </SegmentedButton>
              <SegmentedButton active={vizMode === "random"} disabled={!selectedTaskId} onClick={() => setVizMode("random")}>
                {txt.random}
              </SegmentedButton>
            </div>
          </div>
        </div>

        {/* Random Count */}
        {vizMode === "random" && (
          <div className="form-group">
            <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>{txt.randomCount}</label>
            <input 
              className="form-input" 
              type="number" 
              value={randomCount} 
              onChange={e => setRandomCount(Math.min(100, Math.max(1, +e.target.value)))} 
              min="1" max="100" 
              style={{ padding: "4px 10px", fontSize: 12, height: 28 }} 
            />
          </div>
        )}

        {/* Result Status — показываем только для НЕ filtering режима */}
        {(!isScrapingFiltering || dataSource === "scraping") && selectedTask?.task_type !== "filtering" && (
          <div className="form-group">
            <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>
              {txt.resultStatus}
            </label>
            <div style={{ display: "flex", gap: 4 }}>
              <SegmentedButton active={scrapingStatus === "success"} disabled={!selectedTaskId} onClick={() => setScrapingStatus("success")}>
                {txt.success}
              </SegmentedButton>
              <SegmentedButton active={scrapingStatus === "failed"} disabled={!selectedTaskId} onClick={() => setScrapingStatus("failed")}>
                {txt.failedStatus}
              </SegmentedButton>
            </div>
          </div>
        )}

        {/* Фильтр по статусу фильтрации — показываем для filtering задач и scraping_filtering в режиме filtering */}
        {((selectedTask?.task_type === "filtering") || (isScrapingFiltering && dataSource === "filtering")) && (
          <>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>
                {txt.regularFilterStatus}
              </label>
              <div style={{ display: "flex", gap: 4 }}>
                {[
                  { status: "all", label: txt.allStatus },
                  { status: "passed", label: txt.passedStatus },
                  { status: "rejected", label: txt.rejectedStatus },
                ].map(({ status, label }) => (
                  <SegmentedButton
                    key={status}
                    active={filterStatus === status}
                    disabled={!selectedTaskId}
                    onClick={() => setFilterStatus(status)}
                  >
                    {label}
                  </SegmentedButton>
                ))}
              </div>
            </div>

            {/* AI Filter Status */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: isMobile ? 11 : 12 }}>
                {txt.aiFilterStatus}
              </label>
              {getFilteringConfig(selectedTask)?.use_ai ? (
                <div style={{ display: "flex", gap: 4 }}>
                  {[
                    { status: "all", label: txt.allStatus },
                    { status: "passed", label: txt.passedStatus },
                    { status: "rejected", label: txt.rejectedStatus },
                    { status: "not_applicable", label: txt.noAIStatus },
                  ].map(({ status, label }) => (
                    <SegmentedButton
                      key={status}
                      active={aiFilterStatus === status}
                      disabled={!selectedTaskId}
                      onClick={() => setAiFilterStatus(status)}
                      style={{ fontSize: 10 }}
                    >
                      {label}
                    </SegmentedButton>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: "var(--muted)", padding: "6px 10px", background: "#f3f4f6", borderRadius: 8 }}>
                  {txt.aiNotEnabled}
                </div>
              )}
            </div>
          </>
        )}

        {/* AI Prompt */}
        {vizType === "ai" && (
          <div style={{ padding: isMobile ? 12 : 16, background: "#fff", borderRadius: 12, border: "1.5px solid var(--border)", marginBottom: 12, marginTop: 12 }}>
            <label className="form-label" style={{ marginBottom: 6, fontSize: isMobile ? 12 : 13 }}>{txt.aiPrompt}</label>
            
            <div className="info-box" style={{ marginBottom: 12, background: "#f0f7ff", border: "1.5px solid #bfdbfe", marginTop: 12, padding: isMobile ? 12 : 16 }}>
              <span className="info-icon" style={{ fontSize: isMobile ? 16 : 20 }}>&#128161;</span>
              <p style={{ fontSize: isMobile ? 11 : 12, lineHeight: 1.6 }}>
                <strong>{txt.howItWorks}:</strong> {txt.howItWorksDesc}<br/>
                {txt.setFilterKeywordDesc}
              </p>
            </div>

            <div style={{ marginBottom: 10, marginTop: 20 }}>
              <div style={{ fontSize: isMobile ? 10 : 11, color: "var(--muted)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {txt.availableVariables}:
              </div>
              <div className="intent-examples" style={{ gap: isMobile ? 6 : 8 }}>
                {template.aiVariables?.map(({ key, label }) => (
                  <span key={key} className="example-chip" onClick={() => insertPlaceholder(key)} title={label} style={{ fontSize: isMobile ? 11 : 12, padding: isMobile ? "5px 10px" : "6px 13px" }}>
                    [[{key}]] ({label})
                  </span>
                ))}
              </div>
            </div>

            <textarea
              id="aiPrompt"
              className="intent-textarea"
              placeholder={txt.aiPromptPlaceholder}
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              style={{ minHeight: 150, marginTop: 16, fontSize: isMobile ? 13 : 14 }}
            />

            <div style={{ padding: isMobile ? 10 : 12, background: "#f7f8ff", borderRadius: 10, border: "1.5px solid var(--border)", marginTop: 16 }}>
              <label className="form-label" style={{ fontSize: isMobile ? 13 : 14, marginBottom: 4 }}>{txt.filterCheckKeyword}</label>
              <div style={{ fontSize: isMobile ? 11 : 12, color: "var(--muted)", marginBottom: 8, marginTop: 6 }}>
                {txt.filterCheckDesc}
              </div>
              <div style={{ display: "flex", gap: 8, flexDirection: isMobile ? "column" : "row" }}>
                <input 
                  className="form-input" 
                  type="text" 
                  placeholder={txt.keywordPlaceholder} 
                  value={filterKeyword} 
                  onChange={e => setFilterKeyword(e.target.value)} 
                  style={{ paddingLeft: 14, flex: 1, fontSize: isMobile ? 12 : 14 }} 
                />
                <select 
                  className="form-input" 
                  value={filterMode} 
                  onChange={e => setFilterMode(e.target.value)} 
                  style={{ paddingLeft: 10, width: isMobile ? "100%" : 150, flexShrink: 0, fontSize: isMobile ? 12 : 14 }}
                >
                  <option value="first_line">{txt.firstLineOnly}</option>
                  <option value="full_text">{txt.fullText}</option>
                </select>
              </div>
            </div>
          </div>
        )}
        
        
        {error && (
          <div className="toast toast-error" style={{ marginBottom: 12, fontSize: isMobile ? 11 : 13 }}>
            <span>{error}</span>
          </div>
        )}

        <button className="btn-primary" onClick={loadInitialData} disabled={loading || !selectedTaskId} style={{ fontSize: isMobile ? 14 : 15, marginTop: 28, padding: isMobile ? "11px" : "13px" }}>
          {loading ? `${txt.loading}...` : txt.startVisualization}
        </button>
      </div>

      {/* Результаты */}
      {started && currentResult && (
        <div style={{ marginTop: 20 }}>
          {/* Навигация */}
          <div className="rule-card" style={{ padding: isMobile ? "10px 14px" : "14px 18px", marginBottom: 16 }}>
            <div className="rule-card-top" style={{ flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 12, alignItems: "center" }}>
              <button onClick={goToPrev} disabled={currentIndex === 0 || isTransitioning || isAiLoading}
                className="btn-toggle paused" style={{ opacity: currentIndex === 0 ? 0.3 : 1, fontSize: isMobile ? 11 : 13, padding: isMobile ? "6px 12px" : "7px 16px" }}>
                &larr; {txt.prev}
              </button>
              <div style={{ textAlign: "center", order: isMobile ? -1 : 0 }}>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700 }}>{txt.result} {currentIndex + 1} {vizMode === "all" && totalAvailable > 0 && `${txt.of} ${totalAvailable.toLocaleString()}`}</div>
                <div style={{ fontSize: isMobile ? 10 : 11, color: "var(--muted)", marginTop: 2 }}>
                  {txt.loaded}: {loadedCount} {loadingMore && `${txt.loading}...`} {isAtEnd && vizMode === "all" && `(${txt.allLoaded})`}
                </div>
              </div>
              <button onClick={goToNext} disabled={isAtEnd || loadingMore || isTransitioning || isAiLoading}
                className="btn-toggle paused" style={{ opacity: isAtEnd ? 0.3 : loadingMore ? 0.6 : 1, fontSize: isMobile ? 11 : 13, padding: isMobile ? "6px 12px" : "7px 16px" }}>
                {loadingMore ? <><span className="spin">&#8635;</span> {txt.loading}...</> : isAtEnd ? `${txt.end} ` : `${txt.next} `}&rarr;
              </button>
            </div>
          </div>

          {isAtEnd && vizMode === "all" && (
            <div className="toast toast-success" style={{ marginBottom: 16, textAlign: "center", fontSize: isMobile ? 11 : 13, padding: isMobile ? 10 : 14 }}>
              {txt.reachedEnd} {totalAvailable.toLocaleString()} {txt.resultsLoaded}
            </div>
          )}

          {isNextLoading && !isAtEnd && (
            <div className="toast toast-info" style={{ marginBottom: 16, textAlign: "center", background: "#fff3cd", border: "1px solid #fde68a", color: "#92400e", fontSize: isMobile ? 11 : 13, padding: isMobile ? 10 : 14 }}>
              {txt.loadingNextBatch}...
              <div style={{ width: "100%", height: 3, background: "#fde68a", borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
                <div className="loading-bar-animated" style={{ width: "100%", height: "100%", background: "#f59e0b", borderRadius: 2 }} />
              </div>
            </div>
          )}

          {/* Карточка */}
          <div className="section-card" style={{ opacity: isTransitioning ? 0.7 : 1, transition: "opacity 0.15s ease", padding: isMobile ? "14px" : "28px" }}>
            <div className="rule-card-top" style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1.5px solid var(--border)", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 12 }}>
              <div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700 }}>{template.title}</div>
                <div style={{ fontSize: isMobile ? 10 : 11, color: "var(--muted)", marginTop: 2 }}>
                  {txt.status}: {currentResult.status}
                  {currentResult.raw_data?.username && <> &middot; @{currentResult.raw_data.username}</>}
                  {currentResult.parse_job?.url && (
                    <> &middot; <a href={currentResult.parse_job.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)", textDecoration: "underline" }}>{txt.openURL}</a></>
                  )}
                </div>
              </div>
            </div>

            {/* Статусы фильтрации */}
            {(selectedTask?.task_type === "filtering" || isScrapingFiltering) && currentResult && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {currentResult.filter_status !== undefined && (
                  <span className="rule-status-badge" style={{
                    background: currentResult.filter_status === true || currentResult.filter_status === "passed" ? "#bbf7d0" : "#fecaca",
                    color: currentResult.filter_status === true || currentResult.filter_status === "passed" ? "#15803d" : "#991b1b",
                    border: `1px solid ${currentResult.filter_status === true || currentResult.filter_status === "passed" ? "#86efac" : "#fca5a5"}`,
                    fontSize: isMobile ? 10 : 11,
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontWeight: 600
                  }}>
                    {currentResult.filter_status === true || currentResult.filter_status === "passed" ? txt.passedFilter : txt.rejectedFilter}
                  </span>
                )}

                {currentResult.ai_filter_status !== undefined && currentResult.ai_filter_status !== null && (
                  <span className="rule-status-badge" style={{
                    background: currentResult.ai_filter_status === true ? "#bbf7d0" : 
                                currentResult.ai_filter_status === false ? "#fecaca" : "#e5e7eb",
                    color: currentResult.ai_filter_status === true ? "#15803d" : 
                           currentResult.ai_filter_status === false ? "#991b1b" : "#6b7280",
                    border: `1px solid ${currentResult.ai_filter_status === true ? "#86efac" : 
                                       currentResult.ai_filter_status === false ? "#fca5a5" : "#d1d5db"}`,
                    fontSize: isMobile ? 10 : 11,
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontWeight: 600
                  }}>
                    {currentResult.ai_filter_status === true ? txt.aiPassedFilter : 
                     currentResult.ai_filter_status === false ? txt.aiRejectedFilter : txt.noAIFilter}
                  </span>
                )}
              </div>
            )}

            {/* AI Raw Response из filtering-таски */}
            {(selectedTask?.task_type === "filtering" || isScrapingFiltering) && currentResult?.ai_raw_response && 
             currentResult.ai_raw_response !== "__processing__" && (
              <div style={{
                padding: isMobile ? 12 : 16, borderRadius: 12, marginBottom: 16,
                background: currentResult.ai_filter_passed === true ? "#f0fdf4" : 
                            currentResult.ai_filter_passed === false ? "#fef2f2" : "#f0f7ff",
                border: `1.5px solid ${
                  currentResult.ai_filter_passed === true ? "#bbf7d0" : 
                  currentResult.ai_filter_passed === false ? "#fecaca" : "#bfdbfe"
                }`,
              }}>
                <div style={{ 
                  display: "flex", alignItems: "center", gap: 8, 
                  marginBottom: 8, fontSize: isMobile ? 10 : 11, fontWeight: 700,
                  color: currentResult.ai_filter_passed === true ? "#15803d" : 
                         currentResult.ai_filter_passed === false ? "#991b1b" : "#1e40af"
                }}>
                  {txt.aiFilterResponse}
                  <span className="rule-status-badge" style={{ 
                    background: currentResult.ai_filter_passed === true ? "#bbf7d0" : "#fecaca",
                    color: currentResult.ai_filter_passed === true ? "#15803d" : "#991b1b",
                    border: `1px solid ${currentResult.ai_filter_passed === true ? "#86efac" : "#fca5a5"}`,
                    fontSize: isMobile ? 10 : 11
                  }}>
                    {currentResult.ai_filter_passed === true ? txt.passedStatus : txt.rejectedStatus}
                  </span>
                </div>
                <div style={{ 
                  fontSize: isMobile ? 12 : 13, lineHeight: 1.7, 
                  whiteSpace: "pre-wrap", color: "var(--dark)" 
                }}>
                  {currentResult.ai_raw_response}
                </div>
              </div>
            )}


             {/* Previous AI Response из таргет-таски */}
{(selectedTask?.task_type === "filtering" || isScrapingFiltering) && currentResult?.prev_ai_response && (
  <div style={{
    padding: isMobile ? 12 : 16, borderRadius: 12, marginBottom: 16,
    background: "#f0f7ff",
    border: "1.5px solid #bfdbfe",
  }}>
    <div style={{ 
      display: "flex", alignItems: "center", gap: 8, 
      marginBottom: 8, fontSize: isMobile ? 10 : 11, fontWeight: 700,
      color: "#1e40af"
    }}>
      {"PREVIOUSE AI RESPONSE"}
      
    </div>
    <div style={{ 
      fontSize: isMobile ? 12 : 13, lineHeight: 1.7, 
      whiteSpace: "pre-wrap", color: "var(--dark)" 
    }}>
      {currentResult.prev_ai_response}
    </div>
  </div>
)}

            {/* AI Response (live) */}
            {vizType === "ai" && aiPrompt.trim() && (
              <div style={{
                padding: isMobile ? 12 : 16, borderRadius: 12, marginBottom: 16,
                background: isAiLoading ? "#fef3c7" : aiError ? "#fef2f2" : filterPassed === true ? "#f0fdf4" : filterPassed === false ? "#fef2f2" : "#f0f7ff",
                border: `1.5px solid ${isAiLoading ? "#fde68a" : aiError ? "#fecaca" : filterPassed === true ? "#bbf7d0" : filterPassed === false ? "#fecaca" : "#bfdbfe"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: isMobile ? 10 : 11, fontWeight: 700, color: isAiLoading ? "#92400e" : aiError ? "#991b1b" : filterPassed === true ? "#15803d" : filterPassed === false ? "#991b1b" : "#1e40af" }}>
                    {txt.aiAnalysis} {isAiLoading && <span className="spin">&#8635;</span>}
                    {!isAiLoading && !aiError && filterKeyword.trim() && filterPassed !== null && (
                      <span className="rule-status-badge" style={{ background: filterPassed ? "#bbf7d0" : "#fecaca", color: filterPassed ? "#15803d" : "#991b1b", border: `1px solid ${filterPassed ? "#86efac" : "#fca5a5"}`, fontSize: isMobile ? 10 : 11 }}>
                        {filterPassed ? txt.passed : txt.failed}
                      </span>
                    )}
                  </div>
                  {aiTokensInfo && !isAiLoading && (
                    <span style={{ fontSize: isMobile ? 9 : 10, color: "var(--muted)" }}>{txt.cost}: {aiTokensInfo.cost} &middot; {txt.remaining}: {aiTokensInfo.remaining}</span>
                  )}
                </div>
                <div style={{ fontSize: isMobile ? 12 : 13, lineHeight: 1.7, whiteSpace: "pre-wrap", color: aiError ? "#991b1b" : "var(--dark)", maxHeight: isAiLoading ? 300 : "none", overflowY: isAiLoading ? "auto" : "visible" }}>
                  {aiResponse || (isAiLoading ? `${txt.waitingForAI}...` : "")}
                  {isAiLoading && <span className="cursor-blink" style={{ display: "inline-block", width: 2, height: isMobile ? 14 : 16, background: "#92400e", marginLeft: 2, verticalAlign: "middle" }} />}
                </div>
                {aiError && (
                  <div style={{ marginTop: 8 }}>
                    <button onClick={runAIAnalysis} className="btn-toggle paused" style={{ color: "#991b1b", borderColor: "#fecaca", background: "#fff", fontSize: isMobile ? 10 : 11 }}>{txt.retry}</button>
                  </div>
                )}
              </div>
            )}

            {/* Секции шаблона */}
            {template.sections.map((section, idx) => (
              <div key={idx}>{renderSection(section, currentResult, isTransitioning, isMobile, txt)}</div>
            ))}
          </div>

          {/* Прогресс-бар */}
          {totalAvailable > 0 && vizMode === "all" && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: isMobile ? 10 : 11, color: "var(--muted)", marginBottom: 4 }}>
                <span>{txt.loaded}: {loadedCount.toLocaleString()}</span>
                <span>{t.total}: {totalAvailable.toLocaleString()}</span>
              </div>
              <div className="quota-bar-wrap" style={{ width: "100%", height: 4 }}>
                <div className="quota-bar-fill" style={{ width: `${(loadedCount / totalAvailable) * 100}%`, transition: "width 0.3s ease" }} />
              </div>
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 12, padding: isMobile ? 6 : 8, background: "#f8f9fa", borderRadius: 8, fontSize: isMobile ? 10 : 11, color: "var(--muted)" }}>
            {txt.arrowKeysHint}
          </div>
        </div>
      )}

      {started && !results.length && !loading && (
        <div className="empty-state" style={{ marginTop: 20, padding: isMobile ? "40px 16px" : "70px 20px" }}>
          <div className="empty-icon" style={{ fontSize: isMobile ? 36 : 48 }}>&#128236;</div>
          <h3 style={{ fontSize: isMobile ? 16 : 18 }}>{txt.noResultsFound}</h3>
          <p style={{ fontSize: isMobile ? 12 : 14 }}>{txt.noResultsDesc}</p>
        </div>
      )}
    </div>
  );
}

// ==================== УНИВЕРСАЛЬНЫЙ РЕНДЕРЕР СЕКЦИЙ ====================
function renderSection(section, result, isTransitioning, isMobile, txt) {
  if (!result) return null;
  const base = { marginBottom: 16, opacity: isTransitioning ? 0.6 : 1, transition: "opacity 0.2s ease" };

  switch (section.type) {
    case "image": {
      const url = section.getValue(result);
      if (!url) return null;
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <ImageWithLoader src={url} alt={section.label || "Image"} style={{ maxWidth: "100%", maxHeight: section.style?.maxHeight || 400, objectFit: section.style?.objectFit || "contain", borderRadius: section.style?.borderRadius || 12 }} txt={txt} />
        </div>
      );
    }
    case "text": {
      const text = section.getValue(result);
      if (!text) return null;
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <div style={{ padding: section.style?.padding || (isMobile ? 10 : 14), background: section.style?.background || "#f8f9fa", borderRadius: section.style?.borderRadius || 10, fontSize: section.style?.fontSize || (isMobile ? 12 : 14), lineHeight: section.style?.lineHeight || 1.7, whiteSpace: "pre-wrap", maxHeight: section.style?.maxHeight || "none", overflowY: section.style?.overflowY || "visible" }}>{text}</div>
        </div>
      );
    }
    case "stats":
      return (
        <div style={{ ...base, display: "grid", gridTemplateColumns: `repeat(${section.items.length}, 1fr)`, gap: isMobile ? 6 : 10 }}>
          {section.items.map(item => (
            <div key={item.key} style={{ textAlign: "center", padding: isMobile ? "10px 6px" : "12px 8px", background: section.style?.background || "#f0f4ff", borderRadius: section.style?.borderRadius || 10 }}>
              {item.icon && <div style={{ fontSize: isMobile ? 16 : 18, marginBottom: 4 }}>{item.icon}</div>}
              <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: section.style?.color || "var(--blue)" }}>{item.getValue(result)}</div>
              <div style={{ fontSize: isMobile ? 9 : 10, color: "var(--muted)", marginTop: 2 }}>{item.label}</div>
            </div>
          ))}
        </div>
      );
    case "author_link": {
      const data = section.getValue(result);
      if (!data) return null;
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="platform-btn" style={{ textDecoration: "none", gap: 10, padding: isMobile ? "10px 14px" : "13px 16px", fontSize: isMobile ? 12 : 13 }}>
            <span style={{ fontSize: isMobile ? 18 : 20 }}>{data.icon || "&#128100;"}</span>
            <div>
              <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700 }}>{data.username}</div>
              <div style={{ fontSize: isMobile ? 12 : 13, color: "var(--blue)", marginTop: 2 }}>{txt.openProfile} &rarr;</div>
            </div>
          </a>
        </div>
      );
    }
    case "profile_header": {
      const data = section.getValue(result);
      if (!data) return null;
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {data.profile_pic_url && (
              <ImageWithLoader 
                src={data.profile_pic_url} 
                alt={data.username || "Profile"} 
                style={{ 
                  width: isMobile ? 48 : 60, 
                  height: isMobile ? 48 : 60, 
                  borderRadius: "50%", 
                  objectFit: "cover",
                  border: "2px solid var(--border)",
                }} 
                txt={txt}
              />
            )}
            <div>
              <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700 }}>@{data.username}</div>
              {data.full_name && <div style={{ fontSize: isMobile ? 11 : 12, color: "var(--muted)", marginTop: 2 }}>{data.full_name}</div>}
              <a href={data.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: isMobile ? 11 : 12, color: "var(--blue)", textDecoration: "none", marginTop: 4, display: "inline-block" }}>
                {txt.openProfile} &rarr;
              </a>
            </div>
          </div>
        </div>
      );
    }
    case "source": {
      const source = section.getValue(result);
      if (!source) return null;
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <div style={{ padding: isMobile ? 14 : 15, background: "#f8f9fa", borderRadius: 8, fontSize: isMobile ? 14 : 15 }}>
            {source.keyword && <div style={{ marginBottom: 4 }}>{txt.keyword}: <strong>{source.keyword}</strong></div>}
            {source.scraped_url && (
              <div style={{ marginBottom: 4, marginTop: 8 }}>
                {txt.viaPost}:{" "}
                <a href={source.scraped_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--blue)" }}>
                  {source.scraped_url.length > 50 ? source.scraped_url.slice(0, 50) + "..." : source.scraped_url}
                </a>
              </div>
            )}
            <div style={{ color: "var(--muted)", fontSize: isMobile ? 14 : 15, marginTop: 8 }}>
              {txt.position}: {source.search_position} | {txt.page}: {source.search_page + 1} | {txt.period}: {formatSearchPeriod(source.search_period)}
            </div>
          </div>
        </div>
      );
    }
    case "meta":
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {section.items.map(item => {
              const val = item.getValue(result);
              if (!val && val !== 0) return null;
              const content = (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: isMobile ? "6px 10px" : "8px 12px", background: "#f8f9fa", borderRadius: 8, fontSize: isMobile ? 11 : 12 }}>
                  {item.icon && <span>{item.icon}</span>}
                  <span style={{ fontSize: isMobile ? 10 : 11, color: "var(--muted)" }}>{item.label}:</span>
                  <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, color: "var(--dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
                </div>
              );
              if (item.isLink) return <a key={item.key} href={val} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "var(--blue)" }}>{content}</a>;
              return <div key={item.key}>{content}</div>;
            })}
          </div>
        </div>
      );

    case "custom": {
          // Для секций с кастомным рендерингом (например, кликабельные ссылки)
          if (section.render) {
            return (
              <div style={base}>
                {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
                {section.render(result)}
              </div>
            );
          }
          return null;
        }

    case "json":
      return (
        <div style={base}>
          {section.label && <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{section.label}</div>}
          <pre style={{ padding: isMobile ? 10 : 14, background: "#1e1e1e", borderRadius: 10, color: "#d4d4d4", fontSize: isMobile ? 10 : 11, overflowX: "auto", maxHeight: 400 }}>{JSON.stringify(section.getValue(result), null, 2)}</pre>
        </div>
      );

    default:
      return null;
  }
}